import * as Sentry from '@sentry/react';
import type { ErrorEvent, Breadcrumb } from '@sentry/react';

// auth-ui handles credentials, MFA codes, and recovery codes. The scrubber
// here is the GATE for shipping — if it ever lets a sensitive field through
// to Sentry, we have a security incident. Vitest spec asserts every rule.

export const SENSITIVE_PATH_PATTERNS: readonly RegExp[] = [
  /\/auth(\/|$|\?)/,
  /\/mfa(\/|$|\?)/,
  /\/oauth(\/|$|\?)/,
  /\/password(\/|$|\?)/,
  /\/recovery(\/|$|\?)/,
  /\/magic-link(\/|$|\?)/,
  /\/otp(\/|$|\?)/,
];

export const SENSITIVE_HEADER_KEYS = new Set(['cookie', 'authorization', 'x-csrf-token']);

export const SENSITIVE_DATA_KEYS = new Set(['password', 'code', 'otp', 'recovery_code', 'token']);

export const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

export function isSensitivePath(url: string | undefined): boolean {
  if (!url) return false;
  return SENSITIVE_PATH_PATTERNS.some(re => re.test(url));
}

function maskEmails<T>(value: T): T {
  if (typeof value === 'string') {
    return value.replace(EMAIL_REGEX, '***@***') as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map(v => maskEmails(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = maskEmails(v);
    }
    return out as unknown as T;
  }
  return value;
}

function stripSensitiveHeaders(headers: Record<string, string> | undefined): typeof headers {
  if (!headers) return headers;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (!SENSITIVE_HEADER_KEYS.has(k.toLowerCase())) out[k] = v;
  }
  return out;
}

function hasSensitiveDataKey(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  for (const k of Object.keys(data as Record<string, unknown>)) {
    if (SENSITIVE_DATA_KEYS.has(k.toLowerCase())) return true;
  }
  return false;
}

export function scrubEvent(event: ErrorEvent): ErrorEvent | null {
  const req = event.request;
  if (req) {
    if (isSensitivePath(req.url)) {
      delete req.data;
      delete req.cookies;
      delete req.query_string;
    }
    req.headers = stripSensitiveHeaders(req.headers);
  }
  if (event.extra) event.extra = maskEmails(event.extra);
  if (event.contexts) event.contexts = maskEmails(event.contexts);
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs
      .map(bc => scrubBreadcrumb(bc))
      .filter((bc): bc is Breadcrumb => bc !== null);
  }
  return event;
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // Drop fetch/xhr breadcrumbs whose URL is sensitive — keep the error,
  // not the request payload that led up to it.
  if (
    (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
    typeof breadcrumb.data?.url === 'string' &&
    isSensitivePath(breadcrumb.data.url)
  ) {
    return null;
  }
  // Drop any breadcrumb whose data contains a sensitive field name.
  if (hasSensitiveDataKey(breadcrumb.data)) return null;
  return breadcrumb;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
    sendDefaultPii: false,
    // Replay is disabled entirely on auth-ui. The risk of capturing a
    // password / MFA code / recovery code keystroke outweighs the value.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
export { Sentry };
